import angular from 'angular';
import { format, parse } from 'date-fns';

import importProcessingUrl from './import-processing.html';

export default class ImportUsersCtrl {
  constructor(
    Authentication,
    Restangular,
    Users,
    Utils,
    PersonaCollection,
    Upload,
    $state,
    $uibModal,
    $q,
    geo
  ) {
    'ngInject';

    Object.assign(this, {
      Authentication,
      Restangular,
      Users,
      Utils,
      PersonaCollection,
      Upload,
      $state,
      $uibModal,
      $q,
      geo,
    });

    this.init();
  }

  init() {
    this._setDefaultLocationSwitchedOffManually = false;
    this._switchedInvite = false;

    this.uploader = angular.copy(this.Upload.uploader);
    this.uploader.url = `${this.Restangular.configuration.baseUrl}/upload/table`;

    this.data = false;
    this.tableRows = null;

    this.timezone = this.geo.timezone;
    this.showCountryCodes = false;
    this.countryCode = this.Utils.getCountryCodeData(null, null, this.geo);
    this.invite = false;

    this.locations = this.PersonaCollection.locations;
    this.locationsOptions = this.locations
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((l) => ({ value: l, label: l.name }));
    this.location = this.locations[0];
    this.setDefaultLocation = Boolean(this.locations.length);

    this.uploader.onSuccessItem = this.onSuccessItem.bind(this);
    this.uploader.onErrorItem = this.onErrorItem.bind(this);
    this.invitingPossible = this.Authentication.user.status !== 'unverified';

    this.onRowSelectChange = this._flipDefaultLocation.bind(this);
  }

  onColTypeChange() {
    const rowTypes = this.tableRows.map((r) => r.type);
    this.showCountryCodes = rowTypes.includes('phone');
    if (!this.invite && !this._switchedInvite && rowTypes.includes('email')) {
      this.invite = true;
      this._switchedInvite = true;
    }

    if (this.invite && !rowTypes.includes('email')) {
      this.invite = false;
      this._switchedInvite = false;
    }

    this._flipDefaultLocation();
  }

  get validationError() {
    if (!this.isNameProvided()) {
      return 'At least first name and last name or full name field is required';
    }

    if (this.colTypesCollide()) {
      return 'Cannot select the same column type more than once';
    }

    if (this.nothingSelected()) {
      return 'No rows selected';
    }

    return undefined;
  }

  _flipDefaultLocation() {
    if (this.isDefaultLocationDisabled()) {
      this.setDefaultLocation = false;
    } else if (!this._setDefaultLocationSwitchedOffManually) {
      this.setDefaultLocation = Boolean(this.locations.length);
    }
  }

  onSuccessItem(__, response) {
    this.error = null;
    this.data = response;
    this.tableRows = this.Utils.transpose2dTable(response).map((cells) => ({ cells }));
  }

  onErrorItem(__, ___, status) {
    if (status === 413 || status === 0) {
      this.error = ['Your attachment is too big to upload to Sling.'];
    } else {
      this.error = [
        'Something went wrong while uploading your file. Are you sure it was a valid .xls, .xlsx or .csv file?',
      ];
    }
  }

  async submit() {
    this.error = null;
    this.form?.$setSubmitted();

    if (this.validationError) {
      throw 'invalid form';
    }

    const serverMap = {};
    const dateFormatMap = {};
    this.tableRows.forEach((row, index) => {
      if (row.type) {
        if (Array.isArray(row.type)) {
          const [type, dateFormat] = row.type;
          serverMap[type] = index;
          dateFormatMap[index] = dateFormat;
        } else {
          serverMap[row.type] = index;
        }
      }
    });

    const data = {
      rows: this.data
        .filter((v, i) => this.tableSelectedColumns[i])
        .map((row) =>
          row.map((cell, cellIndex) =>
            dateFormatMap[cellIndex]
              ? format(parse(cell, dateFormatMap[cellIndex], new Date()), 'yyyy-MM-dd')
              : cell
          )
        ),
      maps: serverMap,
      timezone: this.timezone,
      countryCode: this.countryCode.value,
      countryISOCode: this.countryCode.code,
      invite: this.invitingPossible && this.invite,
    };

    if (this.setDefaultLocation) {
      data.location = { id: this.location.id };
    }

    try {
      await this.Restangular.all('users/upload')
        .withHttpConfig({ ignoreGlobalErrorHandler: true })
        .post(data);
      const modal = this.$uibModal.open({
        size: '620',
        templateUrl: importProcessingUrl,
      });

      modal.result.ignoreCancel().finally(() => {
        this.$state.go('dashboard.manage.users.list');
      });

      this.PersonaCollection.importProcessingModal = modal;
    } catch (e) {
      this.error = [e?.data?.message ?? 'There was a problem with the upload process'];
      throw e;
    }
  }

  isNameProvided() {
    if (this.tableRows) {
      const name = this.tableRows.find((row) => row.type === 'name');
      const lastname = this.tableRows.find((row) => row.type === 'lastname');
      const fullName = this.tableRows.find((row) => row.type === 'fullName');
      return fullName || (name && lastname);
    }
  }

  colTypesCollide() {
    const types = this.tableRows?.map((r) => r.type).filter(Boolean);
    return types?.length && types.length !== this.Utils.uniq(types).length;
  }

  nothingSelected() {
    return this.tableSelectedColumns && !Object.values(this.tableSelectedColumns).some(Boolean);
  }

  isInviteDisabled() {
    return !this.tableRows?.find((row) => row.type === 'email');
  }

  isDefaultLocationDisabled() {
    const locationRow = this.tableRows?.find((row) => row.type === 'location');
    if (locationRow) {
      return locationRow.cells.filter((v, i) => this.tableSelectedColumns[i]).every(Boolean);
    }

    return false;
  }

  setDefaultLocationChanged() {
    if (!this.setDefaultLocation) {
      this._setDefaultLocationSwitchedOffManually = true;
    }
  }

  selectCountryCode(countryCodeData) {
    this.countryCode = countryCodeData;
  }
}
